// =======================================
// SKILLS MATRIX — types, constants, helpers
// =======================================

export type SkillCategory = 'Design' | 'Contract Administration';

export const SKILL_CATEGORIES: SkillCategory[] = ['Design', 'Contract Administration'];

/** Competency statuses in click-to-cycle order. */
export const COMPETENCY_CYCLE = ['None', 'In Training', 'With Signoff', 'Achieved'] as const;
export type CompetencyStatus = typeof COMPETENCY_CYCLE[number];

export const nextStatus = (status: CompetencyStatus): CompetencyStatus =>
  COMPETENCY_CYCLE[(COMPETENCY_CYCLE.indexOf(status) + 1) % COMPETENCY_CYCLE.length];

/** Cell labels — full status words; a 'None' cell reads as blank (dash). */
export const STATUS_CELL_LABELS: Record<CompetencyStatus, string> = {
  'None': '–',
  'In Training': 'In Training',
  'With Signoff': 'With Signoff',
  'Achieved': 'Achieved',
};

export const STATUS_DESCRIPTIONS: Record<CompetencyStatus, string> = {
  'None': 'Has not engaged in this work',
  'In Training': 'Performs tasks with close monitoring and checking by a senior',
  'With Signoff': 'Performs tasks but needs final review before sending to a client',
  'Achieved': 'Performs tasks independently and can train other staff',
};

export const statusSlug = (status: CompetencyStatus): string =>
  status.toLowerCase().replace(/\s+/g, '-');

// --- Shapes returned by the get_skills_matrix RPC ---

export interface MatrixMember {
  id: string;
  first_name: string;
  last_name: string;
  type: 'Engineer' | 'Draftsperson';
}

export interface MatrixSkill {
  id: number;
  name: string;
  key_standards: string | null;
  parent_skill_id: number | null;
  sort_order: number;
}

export interface MatrixSection {
  id: number;
  category: SkillCategory;
  name: string;
  sort_order: number;
  skills: MatrixSkill[];
}

export interface MatrixAssignment {
  team_member_id: string;
  skill_id: number;
  status: CompetencyStatus;
  assessed_by: string | null;
  updated_at: string;
}

export interface SkillsMatrixData {
  members: MatrixMember[];
  sections: MatrixSection[];
  assignments: MatrixAssignment[];
}

/** Key for the (member, skill) → status map. */
export const cellKey = (memberId: string, skillId: number): string => `${memberId}:${skillId}`;

/**
 * Contract Administration is assessed for Engineers only — Draftsperson cells
 * in that category are rendered greyed-out and non-interactive.
 */
export const isCellApplicable = (member: MatrixMember, category: SkillCategory): boolean =>
  !(member.type === 'Draftsperson' && category === 'Contract Administration');
